import { useEffect, useRef, useState } from "react";
import { EQUIPMENT_CHEST_ODDS, moduleArt, itemById, moduleById } from "@zend/content";
import { api } from "../api/http.js";
import { onWs } from "../api/ws.js";
import { pick, useT } from "../i18n/index.js";
import { navigate } from "../router.js";
import { GameIcon } from "../assets/gameIcons.js";
import { Icon } from "../ui/Icon.js";
import { Button } from "../ui/Button.js";
import { Timer } from "../ui/Timer.js";
import { Dialog } from "../ui/Dialog.js";
import { useUnits } from "../units/state.js";
import { useSession } from "../state/session.js";
import { AsyncToggle } from "../ui/AsyncToggle.js";
import { useBalances } from "../state/balances.js";
import type { ModuleView } from "../units/types.js";
import { worldApi } from "./api.js";
import { useWorld } from "./state.js";

interface OperationView {
  dayKey: string;
  resetsAt: number;
  kills: number;
  credits: number;
  drawPrice: number;
  drawsLeft: number;
  milestones: { kills: number; itemId: string; claimed: boolean }[];
  chests: { itemId: string; qty: number }[];
}
type OperationReward = { itemId?: string; qty?: number; module?: ModuleView };
export function OperationsView({initialTab="hunt"}: {initialTab?: "hunt" | "chests" | "draw"}) {
  const t = useT();
  const [view, setView] = useState<OperationView | null>(null),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false),
    [confirm, setConfirm] = useState<{ kind: "draw" | "open"; itemId?: string } | null>(null),
    [reward, setReward] = useState<OperationReward | null>(null),
    [level, setLevel] = useState(1),
    [tab, setTab] = useState<"hunt" | "chests" | "draw">(initialTab);
  const alive = useRef(true),
    lock = useRef(false),
    pending = useRef<{ key: string; endpoint: string; body: Record<string, unknown> } | null>(null);
  const refresh = async () => {
    try {
      const v = await api<OperationView>("GET", "/api/operations");
      if (alive.current) setView(v);
    } catch {
      if (alive.current)
        setError(pick({ de: "Einsätze konnten nicht geladen werden.", en: "Could not load operations." }));
    }
  };
  useEffect(() => {
    alive.current = true;
    void refresh();
    const off = onWs("march.update", () => void refresh()),
      timer = setInterval(() => void refresh(), 30000);
    return () => {
      alive.current = false;
      off();
      clearInterval(timer);
    };
  }, []);
  const act = async (endpoint: string, body: Record<string, unknown>, key: string) => {
    if (lock.current) return;
    lock.current = true;
    setBusy(true);
    setError("");
    if (pending.current?.key !== key)
      pending.current = { key, endpoint, body: { ...body, nonce: crypto.randomUUID() } };
    try {
      const r = await api<{ reward: OperationReward; view: OperationView }>(
        "POST",
        pending.current.endpoint,
        pending.current.body,
      );
      pending.current = null;
      if (alive.current) {
        setReward(r.reward);
        setView(r.view);
        setConfirm(null);
      }
      useBalances.getState().applyCreditChange(r.view.credits, "operation");
      void useUnits
        .getState()
        .refreshRoster()
        .catch(() => {});
      void useUnits
        .getState()
        .refreshMe()
        .catch(() => {});
    } catch (e) {
      if (alive.current) setError(e instanceof Error ? e.message : String(e));
    } finally {
      lock.current = false;
      if (alive.current) setBusy(false);
    }
  };
  const find = async () => {
    setBusy(true);
    setError("");
    try {
      const tile = await worldApi.nearest("pack", { level });
      if (!tile)
        throw Error(
          pick({
            de: "Kein Rudel dieser Stufe in Reichweite. Wähle eine andere Stufe.",
            en: "No pack of this level in range. Choose another level.",
          }),
        );
      useWorld.getState().select({ x: tile.x, y: tile.y, tile });
      useWorld.getState().setJump({ x: tile.x, y: tile.y });
      navigate("/app/map");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="page operations-view" data-testid="operations-view">
      <header className="operation-heading">
        <h1>{pick({ de: "Einsätze", en: "Operations" })}</h1>
        <Button onClick={() => navigate("/app/reports")}>{pick({ de: "Berichte", en: "Reports" })}</Button>
      </header>
      <nav className="operation-tabs" aria-label={pick({ de: "Einsatzbereiche", en: "Operation sections" })}>
        {(["hunt", "chests", "draw"] as const).map((key) => (
          <button
            key={key}
            type="button"
            aria-pressed={tab === key}
            onClick={() => setTab(key)}
            data-testid={`operation-tab-${key}`}
          >
            <GameIcon
              id={
                key === "hunt" ? "zmb.shambler" : key === "chests" ? "itm.chest_gold" : "ui.equipment.weapon"
              }
              size={44}
            />
            {key === "hunt"
              ? pick({ de: "Jagd", en: "Hunt" })
              : key === "chests"
                ? pick({ de: "Kisten", en: "Chests" })
                : pick({ de: "Tombola", en: "Draw" })}
            {key === "chests" && view?.chests.some((c) => c.qty > 0) ? (
              <b className="badge">{view.chests.reduce((n, c) => n + c.qty, 0)}</b>
            ) : null}
          </button>
        ))}
      </nav>
      {error ? (
        <div role="alert">
          <p>{error}</p>
          <Button
            onClick={() =>
              pending.current
                ? void act(pending.current.endpoint, pending.current.body, pending.current.key)
                : void refresh()
            }
            disabled={busy}
          >
            {pick({ de: "Erneut versuchen", en: "Try again" })}
          </Button>
        </div>
      ) : null}
      {tab === "hunt" ? (
        <div className="operation-panel" data-testid="hunt-panel">
          <section className="hunt-hero">
            <div className="hunt-art" aria-hidden="true">
              <GameIcon id="zmb.shambler" size={290} />
              <GameIcon id="ui.equipment.weapon" size={130} />
            </div>
            <div className="hunt-command">
              <small>{pick({ de: "TÄGLICHE JAGD", en: "DAILY HUNT" })}</small>
              <h2>{pick({ de: "Sektor säubern", en: "Clear the sector" })}</h2>
              {view ? (
                <div className="hunt-clock">
                  <b>{view.kills}/6</b> {pick({ de: "Siege", en: "wins" })}
                  <span>
                    <Icon name="refresh" size={12}/> <Timer finishesAt={view.resetsAt} />
                  </span>
                </div>
              ) : null}
              <div className="operation-actions">
                <label>
                  {pick({ de: "Stufe", en: "Level" })}{" "}
                  <select value={level} onChange={(e) => setLevel(Number(e.target.value))}>
                    {Array.from({ length: 40 }, (_, i) => (
                      <option key={i + 1}>{i + 1}</option>
                    ))}
                  </select>
                </label>
                <Button
                  variant="gold"
                  onClick={() => void find()}
                  disabled={busy}
                  data-testid="hunt-find-target"
                >
                  {pick({ de: "Ziel finden", en: "Find target" })}
                </Button>
              </div>
            </div>
          </section>
          <div className="hunt-milestones reward-track">
            {view?.milestones.map((m) => (
              <article
                key={m.kills}
                className={m.claimed ? "claimed" : view.kills >= m.kills ? "claimable" : ""}
              >
                <GameIcon id={m.itemId} size={112} label={pick(itemById(m.itemId).name)} />
                <strong>
                  {Math.min(view.kills, m.kills)}/{m.kills} {pick({ de: "Siege", en: "wins" })}
                </strong>
                <progress
                  value={Math.min(view.kills, m.kills)}
                  max={m.kills}
                  aria-label={pick(itemById(m.itemId).name)}
                />
                <Button
                  variant="gold"
                  disabled={busy || m.claimed || view.kills < m.kills}
                  onClick={() =>
                    void act(
                      "/api/operations/claim",
                      { kills: m.kills },
                      "hunt." + view.dayKey + "." + m.kills,
                    )
                  }
                  data-testid={`hunt-claim-${m.kills}`}
                >
                  {m.claimed
                    ? pick({ de: "Abgeholt", en: "Claimed" })
                    : view.kills >= m.kills
                      ? pick({ de: "Abholen", en: "Claim" })
                      : pick({ de: "Gesperrt", en: "Locked" })}
                </Button>
              </article>
            ))}
          </div>
          <details className="field-details">
            <summary>{pick({ de: "So funktioniert die Jagd", en: "How the hunt works" })}</summary>
            <p>
              {pick({
                de: "Siege gegen Infizierte auf der Weltkarte zählen. Nach 1, 3 und 6 Siegen wartet je eine Ausrüstungskiste. Jede enthält ein fertiges Modul. Neue Jagd um Mitternacht (Berlin).",
                en: "Win against infected on the world map. Claim an equipment chest at 1, 3 and 6 wins. Each holds one finished module. Resets at midnight in Berlin.",
              })}
            </p>
          </details>
        </div>
      ) : null}
      {tab === "chests" ? (
        <section className="equipment-chests operation-panel">
          <h2>{pick({ de: "Deine Kisten", en: "Your chests" })}</h2>
          <div className="hunt-milestones chest-gallery">
            {view?.chests.map((c) => (
              <article key={c.itemId}>
                <GameIcon id={c.itemId} size={160} />
                <h3>{pick(itemById(c.itemId).name)}</h3>
                <b className="chest-quantity">×{c.qty}</b>
                <Button
                  disabled={busy || c.qty < 1}
                  onClick={() => setConfirm({ kind: "open", itemId: c.itemId })}
                  data-testid={`equipment-open-${c.itemId}`}
                >
                  {pick({ de: "Öffnen", en: "Open" })}
                </Button>
              </article>
            ))}
          </div>
          <details className="field-details">
            <summary>{pick({ de: "Inhalte & Chancen", en: "Contents & odds" })}</summary>
            <p>
              {pick({
                de: "Ein fertiges Modul pro Kiste. Hersteller und einer von fünf Ausrüstungsplätzen sind innerhalb des Rangs gleich wahrscheinlich.",
                en: "One finished module per chest. Manufacturer and one of five equipment slots are equally likely within the rank.",
              })}
            </p>
            {view?.chests.map((c) => (
              <p key={c.itemId}>
                <b>{pick(itemById(c.itemId).name)}</b>:{" "}
                {EQUIPMENT_CHEST_ODDS[c.itemId]?.map((o) => o.tier + " " + o.pct + "%").join(" · ")}
              </p>
            ))}
          </details>
        </section>
      ) : null}
      {tab === "draw" ? (
        <section className="workshop-draw operation-panel">
          <div className="draw-display" aria-hidden="true">
            <GameIcon id="ui.equipment.weapon" size={130} />
            <GameIcon id="itm.chest_silver" size={210} />
            <GameIcon id="ui.equipment.drive" size={130} />
          </div>
          <div>
            <h2>{pick({ de: "Werkstatt-Tombola", en: "Workshop draw" })}</h2>
            <p>{pick({ de: "1 Ziehung · 1 fertiges Modul", en: "1 draw · 1 finished module" })}</p>
            <div className="draw-odds">
              {EQUIPMENT_CHEST_ODDS["itm.chest_silver"]!.map((o) => (
                <span key={o.tier}>
                  <b>{o.tier}</b> {o.pct}%
                </span>
              ))}
            </div>
            {view ? (
              <>
                <Button
                  variant="gold"
                  disabled={busy || view.drawsLeft < 1 || view.credits < view.drawPrice}
                  onClick={() => setConfirm({ kind: "draw" })}
                  data-testid="equipment-draw"
                >
                  {view.drawPrice} Credits
                </Button>
                <p className="hint">
                  {view.drawsLeft}/5 {pick({ de: "heute übrig", en: "left today" })} · {view.credits} Credits{" "}
                  {pick({ de: "im Besitz", en: "owned" })}
                </p>
              </>
            ) : null}
          </div>
        </section>
      ) : null}
      <nav className="operation-quicklinks" aria-label={pick({ de: "Vorbereitung", en: "Preparation" })}>
        {[
          { path: "squads", icon: "emote.crown", de: "Trupps", en: "Squads" },
          { path: "armory", icon: "ui.equipment.armor", de: "Ausrüstung", en: "Equipment" },
          { path: "repair", icon: "ui.equipment.drive", de: "Reparatur", en: "Repairs" },
        ].map((link) => (
          <button key={link.path} type="button" onClick={() => navigate("/app/units/" + link.path)}>
            <GameIcon id={link.icon} size={40} />
            {pick(link)}
          </button>
        ))}
      </nav>
      {confirm && view ? (
        <Dialog
          title={pick({ de: "Ausrüstung erhalten", en: "Obtain equipment" })}
          onClose={() => {
            if (!busy) setConfirm(null);
          }}
          testId="equipment-confirm"
        >
          <GameIcon id={confirm.itemId ?? "itm.chest_silver"} size={100} />
          <p>
            {confirm.kind === "draw"
              ? `${view.drawPrice} Credits`
              : pick({
                  de: "Verbraucht eine Kiste aus deinem Lager.",
                  en: "Consumes one chest from your inventory.",
                })}
          </p>
          <p>
            {EQUIPMENT_CHEST_ODDS[confirm.itemId ?? "itm.chest_silver"]!.map(
              (o) => o.tier + " " + o.pct + "%",
            ).join(" · ")}
          </p>
          <div className="operation-actions">
            <Button disabled={busy} onClick={() => setConfirm(null)}>
              {pick({ de: "Abbrechen", en: "Cancel" })}
            </Button>
            <Button
              variant="gold"
              loading={busy}
              onClick={() =>
                void act(
                  "/api/operations/" + confirm.kind,
                  confirm.itemId ? { itemId: confirm.itemId } : {},
                  confirm.kind === "draw" ? "draw" : confirm.itemId!,
                )
              }
              data-testid="equipment-confirm-submit"
            >
              {pick({ de: "Einmal bestätigen", en: "Confirm once" })}
            </Button>
          </div>
        </Dialog>
      ) : null}
      {reward ? (
        <Dialog
          title={pick({ de: "Deine Beute", en: "Your loot" })}
          onClose={() => setReward(null)}
          testId="equipment-reward"
        >
          <GameIcon
            id={reward.module ? moduleArt(moduleById(reward.module.moduleId).mfr, reward.module.slot) : (reward.itemId ?? "itm.chest_gold")}
            size={120}
          />
          <h2>
            {reward.module
              ? pick(moduleById(reward.module.moduleId).name)
              : reward.itemId
                ? pick(itemById(reward.itemId).name)
                : ""}
          </h2>
          {reward.module ? (
            <p>
              {reward.module.tier} · {reward.module.mfr} · {t(`module.slot.${reward.module.slot}`)} · +
              {reward.module.main.value}
              {reward.module.main.mode === "pct" ? "%" : ""} {t(`stat.${reward.module.main.stat}`)}
            </p>
          ) : (
            <p>×{reward.qty}</p>
          )}

          {reward.module ? <RewardAutoEquip moduleRowId={reward.module.id} onDone={() => setReward(null)} /> : null}
          <Button
            variant="gold"
            onClick={() => {
              setReward(null);
              if (reward.module) navigate("/app/units");
              else setTab("chests");
            }}
          >
            {reward.module
              ? pick({ de: "Einheit wählen & ausrüsten", en: "Choose unit & equip" })
              : pick({ de: "Zu meinen Kisten", en: "My chests" })}
          </Button>
        </Dialog>
      ) : null}
    </div>
  );
}

/** A drawn module: put it on the best unit right away, or always (D69). */
function RewardAutoEquip({ moduleRowId, onDone }: { moduleRowId: string; onDone: () => void }) {
  const t = useT();
  const autoEquipModule = useUnits((s) => s.autoEquipModule);
  const settings = useSession((s) => s.settings);
  const updateSettings = useSession((s) => s.updateSettings);
  const [busy, setBusy] = useState(false);
  const place = async () => {
    setBusy(true);
    try {
      if (await autoEquipModule(moduleRowId)) onDone();
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="row" style={{ gap: 8, flexWrap: "wrap", justifyContent: "center", marginBottom: 8 }} data-testid="reward-auto-equip">
      <Button variant="primary" disabled={busy || Boolean(settings?.autoEquipModules)} onClick={() => void place()} data-testid="reward-auto-equip-now">
        {t("units.autoEquip.module")}
      </Button>
      <AsyncToggle checked={settings?.autoEquipModules ?? false} save={(autoEquipModules) => updateSettings({ autoEquipModules })} testId="reward-auto-equip-always">
        {t("units.autoEquip.always")}
      </AsyncToggle>
    </div>
  );
}
